import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navigation,
  MessageCircle,
  Calendar,
  Clock,
  MapPin,
  ChevronDown,
  Phone,
  Crown,
  Home as HomeIcon,
  Users,
  Share2,
  Camera,
  HandHeart,
} from 'lucide-react';

import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AvatarGroup, Avatar } from '../ui/Avatar';
import ShareSheet from './ShareSheet';
import { cn, mapsDirectionsUrl, whatsappUrl, fullAddress } from '../../lib/utils';


function PersonRow({ role, roleIcon: Icon, tone, name, phone, avatars }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <AvatarGroup people={avatars} size={38} />
      <div className="min-w-0 flex-1">
        <Badge tone={tone} className="mb-0.5">
          <Icon size={12} /> {role}
        </Badge>
        <p className="truncate text-sm font-semibold text-ink">{name}</p>
      </div>
      <a
        href={whatsappUrl(phone)}
        target="_blank"
        rel="noreferrer"
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/10 text-[#1ebe57] transition-transform duration-200 hover:-translate-y-0.5"
        aria-label={`WhatsApp de ${name}`}
        data-testid="person-whatsapp"
      >
        <Phone size={16} />
      </a>
    </div>
  );
}

export default function CellCard({ cell, network, distance, index = 0 }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const sup = network?.supervisor;
  const leaderAvatars = cell.leader.avatar2
    ? [cell.leader.avatar, cell.leader.avatar2]
    : [cell.leader.avatar];
  const photoStrip = [...leaderAvatars, cell.host.avatar].filter(Boolean).slice(0, 3);


  return (
    <article
      className="animate-fade-up rounded-2xl border border-slate-200 bg-white p-4 shadow-soft opacity-0"
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
      data-testid={`cell-card-${cell.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: network?.color || '#0284c7' }}
            />
            <h3 className="truncate font-heading text-lg font-bold text-ink">{cell.name}</h3>
          </div>
          <p className="mt-0.5 text-xs font-medium text-ink-faint">{network?.name}</p>
          {network?.mission && (
            <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-accent-dark">
              <HandHeart size={12} /> Missão: {network.mission}
            </p>
          )}
        </div>

        {distance != null && (
          <Badge tone="primary" className="shrink-0">
            <MapPin size={12} /> {distance < 1 ? `${Math.round(distance * 1000)} m` : `${distance.toFixed(1)} km`}
          </Badge>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <Calendar size={15} className="text-primary" /> {cell.day}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock size={15} className="text-primary" /> {cell.time}
        </span>
      </div>
      <p className="mt-1.5 flex items-start gap-1.5 text-sm text-ink-soft">
        <MapPin size={15} className="mt-0.5 shrink-0 text-ink-faint" />
        <span>{fullAddress(cell.address)}</span>
      </p>

      {/* Fotos da célula — abre página com fotos maiores */}
      <button
        onClick={() => navigate(`/celula/${cell.id}/fotos`)}
        className="mt-3 flex w-full items-center gap-2.5 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-2 text-left transition-colors duration-200 hover:border-primary/40 hover:bg-primary-light/40"
        data-testid={`cell-photos-${cell.id}`}
        aria-label="Ver fotos da célula"
      >
        <div className="flex shrink-0 -space-x-3">
          {photoStrip.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              loading="lazy"
              className="h-12 w-12 rounded-lg border-2 border-white object-cover"
            />
          ))}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
            <Camera size={15} className="text-primary" /> Fotos da célula
          </p>
          <p className="truncate text-xs text-ink-soft">Líder, anfitrião e últimos encontros</p>
        </div>
        <ChevronDown size={18} className="shrink-0 -rotate-90 text-ink-faint" />
      </button>

      <button
        onClick={() => setOpen((v) => !v)}
        className="mt-2 flex w-full items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5 text-left transition-colors duration-200 hover:bg-slate-100"
        data-testid={`cell-team-toggle-${cell.id}`}
      >

        <div className="flex items-center gap-2">
          <AvatarGroup
            people={[...leaderAvatars, cell.host.avatar, sup?.avatar].filter(Boolean)}
            size={30}
          />
          <span className="text-sm font-semibold text-ink">Ver equipe</span>
        </div>
        <ChevronDown
          size={18}
          className={cn('text-ink-faint transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="mt-1 divide-y divide-slate-100 border-t border-slate-100" data-testid={`cell-team-${cell.id}`}>
          <PersonRow
            role="Líder"
            roleIcon={Crown}
            tone="primary"
            name={cell.leader.name}
            phone={cell.leader.phone}
            avatars={leaderAvatars}
          />
          <PersonRow
            role="Anfitrião"
            roleIcon={HomeIcon}
            tone="accent"
            name={cell.host.name}
            phone={cell.host.phone}
            avatars={[cell.host.avatar]}
          />
          {sup && (
            <PersonRow
              role="Supervisor"
              roleIcon={Users}
              tone="neutral"
              name={sup.name}
              phone={sup.phone}
              avatars={sup.avatar2 ? [sup.avatar, sup.avatar2] : [sup.avatar]}
            />
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button
          as="a"
          href={mapsDirectionsUrl(cell.address)}
          target="_blank"
          rel="noreferrer"
          variant="outline"
          className="flex-1"
          data-testid={`cell-directions-${cell.id}`}
        >
          <Navigation size={17} /> Como Chegar
        </Button>
        <Button
          as="a"
          href={whatsappUrl(
            cell.leader.phone,
            `Olá! Vi a ${cell.name} no app da Águas Vivas e gostaria de participar.`
          )}
          target="_blank"
          rel="noreferrer"
          variant="whatsapp"
          className="flex-1"
          data-testid={`cell-whatsapp-${cell.id}`}
        >
          <MessageCircle size={17} /> WhatsApp
        </Button>
        <Button
          variant="outline"
          className="w-12 shrink-0 px-0"
          onClick={() => setShowShare(true)}
          data-testid={`cell-share-${cell.id}`}
          aria-label="Compartilhar célula"
        >
          <Share2 size={17} />
        </Button>
      </div>


      {showShare && <ShareSheet cell={cell} network={network} onClose={() => setShowShare(false)} />}
    </article>
  );
}
